import { useInfiniteQuery } from "@tanstack/react-query";
import { usePubNub } from "pubnub-react";
import React, { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import Loader from "../components/Loader";
import MessageInput from "../components/MessageInput";
import PoweredBy from "../components/PoweredBy";
import SwitchClinicTip from "../components/SwitchClinicTip";
import useFakebotConvo from "../hooks/useFakebotConvo";
import useUserDetails from "../hooks/useUserDetails";
import {
  getMessages,
  sendFirstMessage,
  sendMessageToOffice,
} from "../utils/messageService";
import {
  delay,
  isOnlyWhiteSpace,
  pubnubCleanUp,
  validateEmail,
} from "../utils/utilityFunctions";
import useGlobalState from "global-state-hooks/useGlobalState";
import InformationCaptureForm from "components/InformationCaptureForm";
import ChatBubble from "components/ChatBubble";

const styles = {
  chat: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    maxHeight: "520px",
  },
  messages: {
    overflowY: "auto",
    display: "flex",
    height: "520px",
    flexDirection: "column-reverse",
  },
};

const greeting = { name: "My name is ", email: "My email address is " };
function ChatV3() {
  const {
    clinic: clinicDetails,
    user: details,
    updateUserDetails: setDetails,
  } = useUserDetails();
  const { clinicIdList } = useGlobalState();
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [validatingMode, setValidatingMode] = useState("");
  const [convo, setConvo] = useState([]);
  const [loading, setLoading] = useState(null);
  const { ref, inView } = useInView();
  const [firstMessage, setFirstMessage] = useState("");

  const [fakebotConvo, setFakebotConvo] = useFakebotConvo(clinicDetails?.id);
  const pubnub = usePubNub();

  const { data, fetchNextPage, isFetchingNextPage, isLoading, refetch } =
    useInfiniteQuery(
      ["convo", details?.token],
      async ({ pageParam = 1, queryKey }) => {
        const { data } = await getMessages({
          chatToken: queryKey[1],
          pageParam,
        });
        return data;
      },
      {
        getNextPageParam: (lastPage, allPages) => {
          const nextPage = allPages?.length + 1;
          return lastPage?.data?.length > 0 ? nextPage : undefined;
        },
        enabled: !!details?.token,
      }
    );

  const updateScroll = () => {
    document
      .getElementById("last-message")
      .scrollIntoView({ behavior: "smooth" });
  };

  const lastElementRef = useCallback((node) => {
    if (node?.id === "scroll-into-view")
      node.scrollIntoView({ behavior: "smooth" });
  }, []);
  const { clinicId } = useGlobalState();
  const submitMessage = async (file) => {
    if (loading) return;
    try {
      if (!loading) setLoading(true);
      await sendMessageToOffice({
        current_office: clinicId,
        chatToken: details?.token,
        message: message,
        file,
      });

      setMessage("");
      if (refetch) await refetch();
      setLoading(false);
    } catch (err) {
      setMessage("");
      console.log(err);
      setLoading(false);
      if (err.response) {
        console.log(
          "client received an error response (5xx, 4xx)",
          err.response
        );
      } else if (err.request) {
        console.log("client never received a response, or request never left");
      } else {
        console.log("Other errors");
      }
    }
  };

  const fakeBotResponse = async (tempConvo) => {
    setLoading(true);
    await delay(300);
    setLoading(false);
  };

  const onClickSend = (file) => {
    if (details?.token) {
      submitMessage(file);
    } else {
      setDetails({ ...details, firstMessage: message });
      setFirstMessage(message);
    }
  };

  const handleInputMessage = (e) => {
    if (validatingMode !== "") {
      if (e.target.value === "") {
        setMessage(greeting[validatingMode]);
        return;
      }

      if (!e.target.value.includes(greeting[validatingMode])) return;

      let string = e.target.value.replace(greeting[validatingMode], "");
      if (string !== greeting[validatingMode].slice(0, -1)) {
        setMessage(`${greeting[validatingMode]}${string}`);
      } else {
        setMessage(greeting[validatingMode]);
      }
    } else {
      setMessage(e.target.value);
    }
  };
  useEffect(() => {
    let arr = [];
    data?.pages?.map((item) => {
      item?.data?.map((log) => {
        console.log(log);
        arr.push(log);
      });
    });
    setFirstMessage(arr[arr.length - 1]?.body);
    arr.pop();
    setConvo([...arr]);
  }, [data, clinicDetails]);

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView]);

  useEffect(() => {
    // if (!details?.token) setFakebotConvo([]);
    const listeners = {
      message: () => refetch(),
    };
    pubnub.addListener(listeners);
    pubnub.subscribe({ channels: [details?.lead_id] });
    return () => {
      pubnubCleanUp(pubnub, listeners);
    };
  }, [details?.lead_id, refetch, pubnub]);

  useEffect(() => {
    if (details) {
      setFirstMessage(details?.firstMessage);
    }
  }, [details]);
  const submitVisitorInformation = async (values) => {
    console.log(values);

    let newDetails;
    setLoading(true);

    await sendFirstMessage({
      clinicId: clinicDetails.id,
      email: values.email,
      fullName: values.fullName,
      phone: values?.mobileNo,
      firstMessage,
    })
      .then((e) => {
        if (e.data?.chat?.id) {
          newDetails = {
            ...details,
            token: e.data.token,
            lead_id: e.data.chat.patient_leads[0].id,
            email: values.email,
            full_name: values.fullName,
            phone: values?.mobileNo,
          };
        } else {
          newDetails = {
            ...details,
            token: e.data.token,
            lead_id: e.data.lead_id,
            email: values.email,
            fullName: values.fullName,
            phone: values?.mobileNo,
          };
        }
        setDetails(newDetails);
      })
      .catch((e) => console.log(e));

    setLoading(false);
  };

  return (
    <div style={styles.chat}>
      <Loader isLoading={details?.token && (isLoading || isFetchingNextPage)} />
      <div style={styles.messages} id="chat-messages">
        {clinicIdList?.length > 1 && <SwitchClinicTip />}

        <PoweredBy />
        {convo?.map((item, index) => {
          return (
            <ChatBubble
              key={item?.id || index}
              id={item.id}
              body={item.body}
              direction={item.direction}
              index={index}
              dataLength={data?.length}
              refProp={ref}
              lastElementRef={lastElementRef}
              clinicDetails={clinicDetails}
              chatwidget_image_url={item.chatwidget_image_url}
            />
          );
        })}
        {details?.token && (
          <ChatBubble
            key="office_hours"
            direction="out"
            clinicDetails={clinicDetails}
            body={
              <>
                Our Office Hours
                <br />
                <br />
                {clinicDetails?.officeHours?.map((item) => {
                  return (
                    <p>
                      {item.day} -{" "}
                      {item?.opened ? (
                        <>
                          {item.opens_at} to {item.closes_at}
                        </>
                      ) : (
                        "Closed"
                      )}
                      <br />
                    </p>
                  );
                })}
              </>
            }
          />
        )}

        {firstMessage && (
          <>
            <InformationCaptureForm
              submitVisitorInformation={submitVisitorInformation}
              userDetails={details}
              loading={loading}
            />
            <ChatBubble
              key={"123"}
              id={"123"}
              body={
                "Please enter your information below while we connect you to a clinic staff"
              }
              direction={"out"}
              clinicDetails={clinicDetails}
            />
          </>
        )}
        {firstMessage && (
          <ChatBubble
            key={"first_message"}
            id={"first_message"}
            body={firstMessage}
            direction={"in"}
            clinicDetails={clinicDetails}
          />
        )}

        <ChatBubble
          key={"Hello! How can I assist you today?"}
          id={"greeting"}
          body={"Hello! How can I assist you today?"}
          direction={"out"}
          clinicDetails={clinicDetails}
        />
      </div>

      {((!details?.token && !firstMessage) || details?.token) && (
        <MessageInput
          handleInputMessage={handleInputMessage}
          placeholder={"Type a message here"}
          loading={loading}
          setMessage={setMessage}
          message={message}
          sendMessage={onClickSend}
        />
      )}
    </div>
  );
}

export default ChatV3;
